import type { TaskListType } from "@orsane/schema/task-lists";
import { useQuery } from "@tanstack/react-query";
import { Plus, X } from "lucide-react";
import { useState } from "react";
import { attributesQueryOptions } from "@/queries/queries";
import type { Attribute } from "@/types/attribute";
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from "./ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

function AttributeTag({ label, onRemoveClick }: { label: string; onRemoveClick: (e: React.MouseEvent<HTMLButtonElement>) => void }) {
    return (
        // biome-ignore lint/a11y/noStaticElementInteractions: want onclick
        // biome-ignore lint/a11y/useKeyWithClickEvents: thats why
        <div
            onClick={(e) => e.stopPropagation()}
            className="rounded-sm border bg-neutral-50 text-zinc-600 text-sm flex items-center gap-0.5 select-none"
        >
            <span className="px-1.5 py-0.5 pr-0">{label}</span>
            {/** biome-ignore lint/a11y/useButtonType: thats why */}
            <button onClick={onRemoveClick} className="h-full px-1 rounded-r-sm hover:bg-neutral-200">
                <X className="size-3" />
            </button>
        </div>
    );
}

export default function AttributeSelect({
    value,
    onChange,
    taskListId,
    type,
}: {
    value: Attribute[];
    onChange: (value: Attribute[]) => void;
    taskListId: number | undefined;
    type: TaskListType;
}) {
    const [open, setOpen] = useState(false);
    const { data: attributes } = useQuery(attributesQueryOptions(taskListId, type));

    if (!attributes) {
        return <div>Loading...</div>;
    }

    const builtinAttributes = attributes.filter((attribute) => attribute.type === "builtin");
    const customAttributes = attributes.filter((attribute) => attribute.type !== "builtin");

    const handleSelect = (v: string) => {
        const attribute = attributes.find((attribute) => attribute.id.toString() === v);
        if (attribute) {
            onChange([...value, attribute]);
        } else {
            throw new Error("Attribute not found");
        }
        setOpen(false);
    };

    const handleRemove = (attribute: Attribute) => {
        onChange(value.filter((v) => v.id !== attribute.id));
    };

    const visibleBuiltinAttributes = builtinAttributes.filter((attribute) => !value.some((v) => v.id === attribute.id));
    const visibleCustomAttributes = customAttributes.filter((attribute) => !value.some((v) => v.id === attribute.id));

    return (
        // biome-ignore lint/a11y/noStaticElementInteractions: thats why
        // biome-ignore lint/a11y/useKeyWithClickEvents: thats why
        <div className="flex gap-1 flex-wrap cursor-text" onClick={() => setOpen(true)}>
            {value.map((attribute) => (
                <AttributeTag key={attribute.id} label={attribute.name} onRemoveClick={() => handleRemove(attribute)} />
            ))}
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger
                    render={
                        <button type="button" className="cursor-text appearance-none flex items-center gap-1 text-sm text-zinc-500">
                            <Plus className="size-3" />
                            Add attribute
                        </button>
                    }
                />
                <PopoverContent className="p-0">
                    <Command>
                        <CommandList>
                            <CommandEmpty>All attributes added</CommandEmpty>
                            {visibleBuiltinAttributes.length > 0 && (
                                <CommandGroup heading="Built-in">
                                    {visibleBuiltinAttributes.map((attribute) => (
                                        <CommandItem key={attribute.id} value={attribute.id.toString()} onSelect={handleSelect}>
                                            {attribute.name}
                                        </CommandItem>
                                    ))}
                                </CommandGroup>
                            )}
                            {visibleCustomAttributes.length > 0 && (
                                <CommandGroup heading="Custom">
                                    {visibleCustomAttributes.map((attribute) => (
                                        <CommandItem key={attribute.id} value={attribute.id.toString()} onSelect={handleSelect}>
                                            {attribute.name}
                                        </CommandItem>
                                    ))}
                                </CommandGroup>
                            )}
                        </CommandList>
                    </Command>
                </PopoverContent>
            </Popover>
        </div>
    );
}
